import { useMemo, useCallback, useEffect } from "react"
import { Form, Row, Col, Label, Button, FormFeedback } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import { toast } from "react-toastify"
import { useQueryClient } from "@tanstack/react-query"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import {
  useCreateVehicleFuelUse,
  useEditVehicleFuelUse,
  VEHICLE_FUEL_USE_QUERY_KEY,
} from "hooks/useVehicleFuelUses"
import FormikCreatableField from "components/Common/FormikCreatableField"

const VehicleFuelUsageAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  fuelUsage,
  employees,
  optionLoading,
}) => {
  const queryClient = useQueryClient()
  const { mutateAsync: createFuelUsage, isPending: createLoading } =
    useCreateVehicleFuelUse()

  const { mutateAsync: editFuelUsage, isPending: editLoading } =
    useEditVehicleFuelUse()

  const validationSchema = Yup.object({
    vehicle_id: Yup.mixed().required("Please select vehicle"),
    uses_by_id: Yup.mixed().required("Please select a staff"),
    fuel_qty: Yup.number()
      .required("Please enter fuel quantity")
      .min(0, "Quantity must be >= 0"),
    mileage: Yup.number()
      .required("Please enter price quantity")
      .min(0, "Price must be >= 0"),
    uses_date: Yup.date().required("Please select date"),
    remarks: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        vehicle_id: values.vehicle_id?.value || values.vehicle_id,
      }

      const action = isEdit
        ? editFuelUsage({ id: fuelUsage.id, ...payload })
        : createFuelUsage(payload)
      try {
        const res = await action
        queryClient.invalidateQueries({
          queryKey: [VEHICLE_FUEL_USE_QUERY_KEY, "all"],
          exact: true, // ensures only this exact key is invalidated
        })

        toast.success(
          res?.message ??
            `Fuel Usage ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error("Failed to save Fuel Usage.")
      }
    },
    [isEdit, fuelUsage, editFuelUsage, createFuelUsage, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      uses_by_id: fuelUsage?.uses_by_id || "",
      vehicle_id: fuelUsage?.vehicle_id || "",
      fuel_qty: fuelUsage?.fuel_qty || "",
      mileage: fuelUsage?.mileage || "",
      uses_date: fuelUsage?.uses_date || "",
      remarks: fuelUsage?.remarks || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  // ✅ Set vehicle_id field when editing
  useEffect(() => {
    if (fuelUsage?.vehicle?.id) {
      formik.setFieldValue("vehicle_id", {
        label: `${fuelUsage.vehicle.stock_id} - ${
          fuelUsage.vehicle.full_chassis ?? ""
        }`,
        value: fuelUsage.vehicle.id,
      })
    } else {
      formik.setFieldValue("vehicle_id", null)
    }
  }, [fuelUsage])

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Fuel Usage"}${
      loading ? "..." : ""
    }`
  }, [isEdit, editLoading, createLoading])

  const isSubmitDisabled = useMemo(
    () => optionLoading || editLoading || createLoading,
    [optionLoading, editLoading, createLoading]
  )

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Fuel Usage" : "Add Fuel Usage"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Row className="mb-3">
          <Col sm={12} className="mb-3">
            <VehicleIdSelect formik={formik} />
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="fuel_qty">Quantity (L)</Label>
            <InputField
              type="number"
              name="fuel_qty"
              id="fuel_qty"
              value={formik.values.fuel_qty}
              onChange={formik.handleChange}
              invalid={formik.touched.fuel_qty && !!formik.errors.fuel_qty}
              loading={optionLoading}
            />
            {formik.touched.fuel_qty && formik.errors.fuel_qty && (
              <FormFeedback type="invalid">
                {formik.errors.fuel_qty}
              </FormFeedback>
            )}
          </Col>
          <Col sm={6} className="mb-3">
            <Label htmlFor="mileage">Last Mileage</Label>
            <InputField
              type="number"
              name="mileage"
              id="mileage"
              value={formik.values.mileage}
              onChange={formik.handleChange}
              invalid={formik.touched.mileage && !!formik.errors.mileage}
              loading={optionLoading}
            />
            {formik.touched.mileage && formik.errors.mileage && (
              <FormFeedback type="invalid">
                {formik.errors.mileage}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6}>
            <FormikCreatableField
              name="uses_by_id"
              label="Usage By"
              formik={formik}
              data={employees}
              placeholder="Select a staff"
            />
            {formik.touched.uses_by_id && formik.errors.uses_by_id && (
              <FormFeedback type="invalid">
                {formik.errors.uses_by_id}
              </FormFeedback>
            )}
          </Col>

          <Col sm={6} className="mb-3">
            <Label htmlFor="date">Usage Date</Label>
            <InputField
              type="datetime-local"
              name="uses_date"
              id="uses_date"
              value={formik.values.uses_date}
              onChange={formik.handleChange}
              invalid={formik.touched.uses_date && !!formik.errors.uses_date}
              loading={optionLoading}
            />
            {formik.touched.uses_date && formik.errors.uses_date && (
              <FormFeedback type="invalid">
                {formik.errors.uses_date}
              </FormFeedback>
            )}
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
            loading={optionLoading}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end">
          <Button color="success" type="submit" disabled={isSubmitDisabled}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default VehicleFuelUsageAddEdit
